import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Play, RotateCcw, Music, Volume2, StopCircle } from 'lucide-react';
import { toast } from 'sonner';
import { convertToDirectAudioUrl } from '@/lib/AudioUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BACKGROUND_AUDIOS = [
  { value: 'none', label: 'Ninguno' },
  { value: 'balance', label: 'Balance' },
  { value: 'delta', label: 'Delta' },
  { value: 'focus', label: 'Focus' },
  { value: 'gamma', label: 'Gamma' },
  { value: 'study', label: 'Study' }
];

const BreathingSession = () => {
  const { patternId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  
  const [pattern, setPattern] = useState(null);
  const [state, setState] = useState('READY');
  const [currentBreath, setCurrentBreath] = useState(0);
  const [currentRound, setCurrentRound] = useState(1);
  const [countdown, setCountdown] = useState(15);
  const [apneaTime, setApneaTime] = useState(0);
  const [meditationTime, setMeditationTime] = useState(0);
  const [meditationDuration, setMeditationDuration] = useState(10);
  const [apneaRecords, setApneaRecords] = useState([]);
  const [totalTime, setTotalTime] = useState(0);
  const [clickCount, setClickCount] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [selectedBgAudio, setSelectedBgAudio] = useState('none');
  const [stopClickCount, setStopClickCount] = useState(0);
  const [breathAudioVolume, setBreathAudioVolume] = useState(() => {
    const saved = localStorage.getItem('breathAudioVolume');
    return saved ? parseInt(saved) : 40;
  });
  const [bgAudioVolume, setBgAudioVolume] = useState(() => {
    const saved = localStorage.getItem('bgAudioVolume');
    return saved ? parseInt(saved) : 60;
  });
  const [customAudioVolume, setCustomAudioVolume] = useState(() => {
    const saved = localStorage.getItem('customAudioVolume');
    return saved ? parseInt(saved) : 70;
  });
  const [customAudioEchoIntensity, setCustomAudioEchoIntensity] = useState(() => {
    const saved = localStorage.getItem('customAudioEchoIntensity');
    return saved ? parseInt(saved) : 0; // 0 = no echo, 100 = max echo
  });
  const [sessionStartTime, setSessionStartTime] = useState(null);
  
  // Apoyo y Enfoque states
  const [apoyoEnabled, setApoyoEnabled] = useState(() => {
    const saved = localStorage.getItem('apoyoEnabled');
    return saved ? saved === 'true' : false;
  });
  const [enfoqueEnabled, setEnfoqueEnabled] = useState(() => {
    const saved = localStorage.getItem('enfoqueEnabled');
    return saved ? saved === 'true' : false;
  });
  const [apoyoVolume, setApoyoVolume] = useState(() => {
    const saved = localStorage.getItem('apoyoVolume');
    return saved ? parseInt(saved) : 50;
  });
  const [enfoqueVolume, setEnfoqueVolume] = useState(() => {
    const saved = localStorage.getItem('enfoqueVolume');
    return saved ? parseInt(saved) : 50;
  });
  
  // Audio activation for mobile
  const [audioActivated, setAudioActivated] = useState(false);
  
  // Use refs to access latest state values in timers (avoid stale closures)
  const apoyoEnabledRef = useRef(apoyoEnabled);
  const enfoqueEnabledRef = useRef(enfoqueEnabled);
  const apoyoVolumeRef = useRef(apoyoVolume);
  const enfoqueVolumeRef = useRef(enfoqueVolume);
  const stateRef = useRef(state);
  
  // Update refs when states change
  useEffect(() => {
    apoyoEnabledRef.current = apoyoEnabled;
    console.log('📊 apoyoEnabled changed to:', apoyoEnabled);
  }, [apoyoEnabled]);
  
  useEffect(() => {
    enfoqueEnabledRef.current = enfoqueEnabled;
    console.log('📊 enfoqueEnabled changed to:', enfoqueEnabled);
  }, [enfoqueEnabled]);
  
  useEffect(() => {
    apoyoVolumeRef.current = apoyoVolume;
  }, [apoyoVolume]);
  
  useEffect(() => {
    enfoqueVolumeRef.current = enfoqueVolume;
  }, [enfoqueVolume]);
  
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  
  const mounted = useRef(true);
  const wakeLock = useRef(null);
  const audioContext = useRef(null);
  const audioSource = useRef(null);
  const audioGainNode = useRef(null);
  const convolver = useRef(null);
  const breathFire1and2AudioRef = useRef(null);
  const breathFire3AudioRef = useRef(null);
  const breathFire4AudioRef = useRef(null);
  const largamosun20AudioRef = useRef(null);
  const largamostodoelireAudioRef = useRef(null);
  const beginAudioRef = useRef(null);
  const finalizeAudioRef = useRef(null);
  const backgroundAudioRef = useRef(null);
  const customApneaAudioRef = useRef(null);
  
  // Apoyo audios (energia 1-8)
  const energiaAudioRefs = useRef([]);
  
  // Enfoque audios (apnea 1-12)
  const apneaAudioRefs = useRef([]);
  
  // Tracking de audios reproducidos en la ronda actual (para evitar repeticiones)
  const playedEnergiaAudios = useRef([]);
  const playedApneaAudios = useRef([]);
  
  // Flags para prevenir múltiples inicializaciones
  const apoyoTimerStarted = useRef(false);
  const enfoqueTimerStarted = useRef(false);
  
  const timers = useRef({});

  useEffect(() => {
    mounted.current = true;
    loadPattern();
    
    return () => {
      mounted.current = false;
      Object.values(timers.current).forEach(t => {
        if (t) {clearTimeout(t); clearInterval(t);}
      });
      stopAllAudios();
      releaseWakeLock();
      
      // Cleanup Web Audio API on unmount
      if (audioSource.current) {
        try {
          audioSource.current.disconnect();
          audioSource.current = null;
        } catch(e) {}
      }
      if (audioContext.current) {
        try {
          audioContext.current.close();
        } catch(e) {}
      }
    };
  }, []);

  // Initialize volumes when audio elements are loaded
  useEffect(() => {
    if (pattern) {
      // Small delay to ensure audio elements are mounted
      setTimeout(() => {
        // Set breath audio volumes
        const breathAudios = [
          breathFire1and2AudioRef,
          breathFire3AudioRef,
          breathFire4AudioRef,
          largamosun20AudioRef,
          largamostodoelireAudioRef,
          beginAudioRef,
          finalizeAudioRef
        ];
        
        breathAudios.forEach(ref => {
          if (ref.current) {
            ref.current.volume = breathAudioVolume / 100;
          }
        });

        // Set background audio volume
        if (backgroundAudioRef.current) {
          backgroundAudioRef.current.volume = bgAudioVolume / 100;
        }

        // Set custom audio volume
        if (customApneaAudioRef.current) {
          customApneaAudioRef.current.volume = customAudioVolume / 100;
        }

        console.log('✓ Initial volumes set:', {
          breath: breathAudioVolume + '%',
          septaSync: bgAudioVolume + '%',
          custom: customAudioVolume + '%'
        });
      }, 100);
    }
  }, [pattern, breathAudioVolume, bgAudioVolume, customAudioVolume]);

  // Update volumes dynamically during session
  useEffect(() => {
    // Update all breath audio volumes
    const breathAudios = [
      breathFire1and2AudioRef,
      breathFire3AudioRef,
      breathFire4AudioRef,
      largamosun20AudioRef,
      largamostodoelireAudioRef,
      beginAudioRef,
      finalizeAudioRef
    ];
    
    breathAudios.forEach(ref => {
      if (ref.current) {
        ref.current.volume = breathAudioVolume / 100;
      }
    });
    console.log('✓ Updated breath audio volume to:', breathAudioVolume + '%');
  }, [breathAudioVolume]);

  useEffect(() => {
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.volume = bgAudioVolume / 100;
      console.log('✓ Updated SeptaSync volume to:', bgAudioVolume + '%');
    }
  }, [bgAudioVolume]);

  useEffect(() => {
    if (customAudioEchoIntensity > 0 && audioGainNode.current) {
      // Update volume through Web Audio API gain node
      audioGainNode.current.gain.value = customAudioVolume / 100;
      console.log('✓ Updated custom audio volume (with echo) to:', customAudioVolume + '%');
    } else if (customApneaAudioRef.current) {
      // Update volume directly
      customApneaAudioRef.current.volume = customAudioVolume / 100;
      console.log('✓ Updated custom audio volume to:', customAudioVolume + '%');
    }
  }, [customAudioVolume, customAudioEchoIntensity]);

  // Update echo intensity dynamically
  useEffect(() => {
    if (customAudioEchoIntensity > 0 && audioContext.current) {
      updateEchoIntensity(customAudioEchoIntensity);
    }
  }, [customAudioEchoIntensity]);

  const stopAllAudios = () => {
    [backgroundAudioRef, customApneaAudioRef].forEach(ref => {
      if (ref.current) {
        try {
          ref.current.pause();
          ref.current.currentTime = 0;
        } catch(e) {}
      }
    });
  };

  // Wake Lock API to keep screen on during session
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLock.current = await navigator.wakeLock.request('screen');
        console.log('✓ Wake Lock activated - screen will stay on');
        
        wakeLock.current.addEventListener('release', () => {
          console.log('Wake Lock released');
        });
      }
    } catch (err) {
      console.warn('Wake Lock not supported or failed:', err.message);
    }
  };

  const releaseWakeLock = async () => {
    if (wakeLock.current) {
      try {
        await wakeLock.current.release();
        wakeLock.current = null;
        console.log('Wake Lock released manually');
      } catch (err) {
        console.warn('Error releasing wake lock:', err);
      }
    }
  };

  const loadPattern = async () => {
    try {
      const headers = {};
      const authHeaders = getAuthHeaders();
      if (authHeaders.Authorization) {
        headers.Authorization = authHeaders.Authorization;
      }
      
      const response = await axios.get(`${API}/patterns`, { headers });
      const found = response.data.find(p => p.id === patternId);
      
      if (found) {
        setPattern(found);
        if (found.name === "Meditación con audios SeptaSync") {
          setMeditationDuration(Math.floor(found.apnea_duration / 60) || 10);
        }
      } else {
        toast.error('Patrón no encontrado');
        navigate('/');
      }
    } catch (error) {
      toast.error('Error al cargar patrón');
      navigate('/');
    }
  };

  const playAudio = (audioRef) => {
    if (audioRef.current && mounted.current) {
      try {
        const audioName = audioRef.current.src.split('/').pop();
        console.log(`🔊 Playing audio: ${audioName}`);
        audioRef.current.volume = breathAudioVolume / 100;
        audioRef.current.currentTime = 0;
        audioRef.current.play()
          .then(() => {
            console.log(`✅ Audio playing: ${audioName}`);
          })
          .catch(err => {
            console.error(`❌ Audio play failed: ${audioName}`, err.message);
          });
      } catch (e) {
        console.error('❌ Exception in playAudio:', e.message);
      }
    }
  };

  const playBackgroundAudio = () => {
    if (selectedBgAudio !== 'none' && backgroundAudioRef.current && mounted.current) {
      try {
        backgroundAudioRef.current.loop = true;
        backgroundAudioRef.current.volume = bgAudioVolume / 100;
        backgroundAudioRef.current.play().catch(() => {});
      } catch (e) {}
    }
  };

  const playCustomApneaAudio = (useFadeIn = false) => {
    if (!user?.custom_audio_url || !customApneaAudioRef.current || !mounted.current) {
      return;
    }
    
    try {
      console.log('Playing custom apnea audio via proxy for user:', user.id, 'Fade-in:', useFadeIn);
      
      // Use backend proxy endpoint to bypass CORS issues
      const proxyUrl = `${API}/audio/custom/${user.id}`;
      console.log('Proxy URL:', proxyUrl);
      
      // Only set src if it hasn't been set yet (first time)
      if (!customApneaAudioRef.current.src || !customApneaAudioRef.current.src.includes(user.id)) {
        customApneaAudioRef.current.src = proxyUrl;
      }
      
      // CRITICAL: Ensure audio is not muted
      customApneaAudioRef.current.muted = false;
      customApneaAudioRef.current.loop = true;
      
      // If echo intensity > 0, use Web Audio API
      if (customAudioEchoIntensity > 0) {
        initAudioContext();
        
        if (audioContext.current && convolver.current && audioGainNode.current) {
          // Only create MediaElementSource if it doesn't exist yet
          // MediaElementSource can only be created once per audio element
          if (!audioSource.current) {
            console.log('Creating MediaElementSource for first time');
            audioSource.current = audioContext.current.createMediaElementSource(customApneaAudioRef.current);
            
            // Connect: source → gain → convolver → destination
            audioSource.current.connect(audioGainNode.current);
            audioGainNode.current.connect(convolver.current);
            convolver.current.connect(audioContext.current.destination);
          }
          
          // Set initial volume (0 for fade-in, or target volume)
          if (useFadeIn) {
            audioGainNode.current.gain.value = 0;
          } else {
            audioGainNode.current.gain.value = customAudioVolume / 100;
          }
          
          console.log('✓ Echo effect enabled for custom audio at', customAudioEchoIntensity + '%');
        }
      } else {
        // No echo: use standard volume control
        if (useFadeIn) {
          customApneaAudioRef.current.volume = 0; // Start silent for fade-in
        } else {
          customApneaAudioRef.current.volume = customAudioVolume / 100;
        }
      }
      
      // Reset to beginning and play
      customApneaAudioRef.current.currentTime = 0;
      const playPromise = customApneaAudioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('✓ Custom apnea audio playing successfully', customAudioEchoIntensity > 0 ? '(with echo)' : '(no echo)');
            
            // If fade-in requested, gradually increase volume over 2 seconds
            if (useFadeIn) {
              console.log('🎚️ Starting fade-in over 2 seconds...');
              const targetVolume = customAudioVolume / 100;
              const steps = 20; // 20 steps over 2 seconds = 100ms per step
              const volumeIncrement = targetVolume / steps;
              let currentStep = 0;
              
              const fadeInterval = setInterval(() => {
                currentStep++;
                const newVolume = volumeIncrement * currentStep;
                
                if (customApneaAudioRef.current && mounted.current && stateRef.current === 'APNEA') {
                  if (customAudioEchoIntensity > 0 && audioGainNode.current) {
                    audioGainNode.current.gain.value = newVolume;
                  } else {
                    customApneaAudioRef.current.volume = newVolume;
                  }
                  
                  if (currentStep >= steps) {
                    clearInterval(fadeInterval);
                    console.log('✅ Fade-in complete at', (targetVolume * 100).toFixed(0) + '%');
                  }
                } else {
                  clearInterval(fadeInterval);
                  console.log('⚠️ Fade-in cancelled (state changed or unmounted)');
                }
              }, 100);
              
              // Store interval ID for cleanup
              timers.current.fadeIn = fadeInterval;
            }
          })
          .catch(error => {
            console.warn('Cannot play custom audio:', error.message);
            toast.error('No se pudo reproducir el audio personalizado');
          });
      }
    } catch (e) {
      console.warn('Exception playing custom audio:', e.message);
    }
  };

  const stopCustomApneaAudio = () => {
    if (customApneaAudioRef.current) {
      try {
        customApneaAudioRef.current.pause();
        customApneaAudioRef.current.currentTime = 0;
        console.log('✓ Custom audio paused');
      } catch(e) {}
    }
    
    // DO NOT disconnect Web Audio API source - it cannot be recreated
    // The source stays connected for reuse in next apnea
  };

  // Apoyo: Play random energia audio (during breathing) - NO REPETIR en misma ronda
  const playRandomEnergiaAudio = () => {
    const enabled = apoyoEnabledRef.current;
    const volume = apoyoVolumeRef.current;
    
    console.log('🎵 Attempting to play energia audio. Apoyo enabled:', enabled, 'Mounted:', mounted.current);
    
    if (!enabled || !mounted.current) {
      console.log('❌ Cannot play energia: apoyo disabled or not mounted');
      return;
    }
    
    // Si ya se reprodujeron todos los audios, resetear la lista
    if (playedEnergiaAudios.current.length >= 8) {
      console.log('🔄 All energia audios played, resetting list for new cycle');
      playedEnergiaAudios.current = [];
    }
    
    // Crear array de índices disponibles (no reproducidos aún)
    const availableIndices = [];
    for (let i = 0; i < 8; i++) {
      if (!playedEnergiaAudios.current.includes(i)) {
        availableIndices.push(i);
      }
    }
    
    console.log('🎲 Available energia audios:', availableIndices, 'Already played:', playedEnergiaAudios.current);
    
    // Seleccionar uno aleatorio de los disponibles
    const randomIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
    const audioRef = energiaAudioRefs.current[randomIndex];
    
    console.log('🎵 Selected energia audio index:', randomIndex, 'Ref exists:', !!audioRef);
    
    if (audioRef) {
      try {
        // Marcar como reproducido
        playedEnergiaAudios.current.push(randomIndex);
        
        audioRef.volume = volume / 100;
        audioRef.currentTime = 0;
        audioRef.play()
          .then(() => console.log(`✅ Playing energia${randomIndex + 1}.mp3 at ${volume}% (${playedEnergiaAudios.current.length}/8 played)`))
          .catch(e => console.warn('❌ Cannot play energia audio:', e.message));
      } catch(e) {
        console.warn('❌ Error playing energia audio:', e.message);
      }
    } else {
      console.warn('❌ Energia audio ref not found for index:', randomIndex);
    }
  };

  // Enfoque: Play random apnea audio (during apnea state) - NO REPETIR en misma ronda
  const playRandomApneaAudio = () => {
    const enabled = enfoqueEnabledRef.current;
    const volume = enfoqueVolumeRef.current;
    
    console.log('🎵 Attempting to play apnea audio. Enfoque enabled:', enabled, 'Mounted:', mounted.current);
    
    if (!enabled || !mounted.current) {
      console.log('❌ Cannot play apnea: enfoque disabled or not mounted');
      return;
    }
    
    // Si ya se reprodujeron todos los audios, resetear la lista
    if (playedApneaAudios.current.length >= 12) {
      console.log('🔄 All apnea audios played, resetting list for new cycle');
      playedApneaAudios.current = [];
    }
    
    // Crear array de índices disponibles (no reproducidos aún)
    const availableIndices = [];
    for (let i = 0; i < 12; i++) {
      if (!playedApneaAudios.current.includes(i)) {
        availableIndices.push(i);
      }
    }
    
    console.log('🎲 Available apnea audios:', availableIndices, 'Already played:', playedApneaAudios.current);
    
    // Seleccionar uno aleatorio de los disponibles
    const randomIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
    const audioRef = apneaAudioRefs.current[randomIndex];
    
    console.log('🎵 Selected apnea audio index:', randomIndex, 'Ref exists:', !!audioRef);
    
    if (audioRef) {
      try {
        // Marcar como reproducido
        playedApneaAudios.current.push(randomIndex);
        
        audioRef.volume = volume / 100;
        audioRef.currentTime = 0;
        audioRef.play()
          .then(() => console.log(`✅ Playing apnea${randomIndex + 1}.mp3 at ${volume}% (${playedApneaAudios.current.length}/12 played)`))
          .catch(e => console.warn('❌ Cannot play apnea audio:', e.message));
      } catch(e) {
        console.warn('❌ Error playing apnea audio:', e.message);
      }
    } else {
      console.warn('❌ Apnea audio ref not found for index:', randomIndex);
    }
  };

  // Start Apoyo timer (first at 10s, then random 10-27s intervals)
  const startApoyoTimer = (isFirst = true) => {
    const enabled = apoyoEnabledRef.current;
    
    console.log('🎬 [APOYO] startApoyoTimer called');
    console.log(`   IsFirst: ${isFirst}, Enabled: ${enabled}, Mounted: ${mounted.current}`);
    console.log(`   Timer already started: ${apoyoTimerStarted.current}`);
    console.log(`   Current state: ${stateRef.current}`);
    
    if (!mounted.current) {
      console.log('❌ [APOYO] Not starting: not mounted');
      return;
    }
    
    if (!enabled) {
      console.log('❌ [APOYO] Not starting: disabled');
      return;
    }
    
    // CRITICAL: If this is the first call, check if timer already started
    if (isFirst && apoyoTimerStarted.current) {
      console.log('⚠️ [APOYO] Timer already started! Ignoring duplicate call');
      return;
    }
    
    // Clear any existing timer to prevent duplicates
    if (timers.current.apoyo) {
      console.log('🧹 [APOYO] Clearing existing timer');
      clearTimeout(timers.current.apoyo);
      timers.current.apoyo = null;
    }
    
    // Mark timer as started on first call
    if (isFirst) {
      apoyoTimerStarted.current = true;
      console.log('🔒 [APOYO] Timer marked as started');
    }
    
    // First audio EXACTLY at 10 seconds, subsequent audios at random 10-27 seconds
    const randomDelay = isFirst ? 10000 : (Math.floor(Math.random() * 17000) + 10000); // 10-27 seconds
    
    const delaySeconds = (randomDelay / 1000).toFixed(1);
    console.log(`📅 [APOYO] Scheduling ${isFirst ? '🔴 FIRST' : '🔵 NEXT'} audio in ${delaySeconds}s`);
    
    timers.current.apoyo = setTimeout(() => {
      const now = new Date().toLocaleTimeString();
      console.log(`⏰ [APOYO] ⏰⏰⏰ Timer FIRED at ${now} ⏰⏰⏰`);
      console.log(`⏰ [APOYO] Current state when fired: ${stateRef.current}`);
      
      // Solo reproducir si estamos en estado de respiración (INHALE o EXHALE)
      const currentState = stateRef.current;
      const isBreathingState = currentState === 'INHALE' || currentState === 'EXHALE';
      
      if (mounted.current && apoyoEnabledRef.current && isBreathingState) {
        console.log('✅ [APOYO] Conditions OK (breathing state), playing audio...');
        playRandomEnergiaAudio();
        console.log('✅ [APOYO] Scheduling next audio...');
        startApoyoTimer(false); // Schedule next one (not first)
      } else {
        console.log(`❌ [APOYO] Conditions FAILED. Mounted: ${mounted.current}, Enabled: ${apoyoEnabledRef.current}, State: ${currentState}, IsBreathingState: ${isBreathingState}`);
        apoyoTimerStarted.current = false; // Reset flag if stopped
      }
    }, randomDelay);
    
    const willFireAt = new Date(Date.now() + randomDelay).toLocaleTimeString();
    console.log(`✅ [APOYO] Timer ID created: ${timers.current.apoyo}`);
    console.log(`✅ [APOYO] Will fire at ${willFireAt} (in ${delaySeconds}s)`);
  };

  // Stop Apoyo timer
  const stopApoyoTimer = () => {
    console.log('🛑 [APOYO] stopApoyoTimer called');
    if (timers.current.apoyo) {
      clearTimeout(timers.current.apoyo);
      timers.current.apoyo = null;
      console.log('🛑 [APOYO] Timer cleared');
    }
    apoyoTimerStarted.current = false; // Reset flag
    console.log('🔓 [APOYO] Timer flag reset');
  };

  // Start Enfoque timer (first at 10s, then random 30-90s intervals - MÁS SEPARADOS)
  const startEnfoqueTimer = (isFirst = true) => {
    const enabled = enfoqueEnabledRef.current;
    
    console.log('🎬 [ENFOQUE] startEnfoqueTimer called');
    console.log(`   IsFirst: ${isFirst}, Enabled: ${enabled}, State: ${state}`);
    console.log(`   Timer already started: ${enfoqueTimerStarted.current}`);
    
    if (!mounted.current) {
      console.log('❌ [ENFOQUE] Not starting: not mounted');
      return;
    }
    
    if (!enabled) {
      console.log('❌ [ENFOQUE] Not starting: disabled');
      return;
    }
    
    // CRITICAL: If this is the first call, check if timer already started
    if (isFirst && enfoqueTimerStarted.current) {
      console.log('⚠️ [ENFOQUE] Timer already started! Ignoring duplicate call');
      return;
    }
    
    // Clear any existing timer to prevent duplicates
    if (timers.current.enfoque) {
      console.log('🧹 [ENFOQUE] Clearing existing timer');
      clearTimeout(timers.current.enfoque);
      timers.current.enfoque = null;
    }
    
    // Mark timer as started on first call
    if (isFirst) {
      enfoqueTimerStarted.current = true;
      console.log('🔒 [ENFOQUE] Timer marked as started');
    }
    
    // First audio EXACTLY at 10 seconds, subsequent audios at random 30-90 seconds
    const randomDelay = isFirst ? 10000 : (Math.floor(Math.random() * 60000) + 30000);
    
    const delaySeconds = (randomDelay / 1000).toFixed(1);
    console.log(`📅 [ENFOQUE] Scheduling ${isFirst ? '🔴 FIRST' : '🔵 NEXT'} audio in ${delaySeconds}s`);
    
    timers.current.enfoque = setTimeout(() => {
      const now = new Date().toLocaleTimeString();
      console.log(`⏰ [ENFOQUE] ⏰⏰⏰ Timer FIRED at ${now} ⏰⏰⏰`);
      
      if (mounted.current && enfoqueEnabledRef.current && state === 'APNEA') {
        console.log('✅ [ENFOQUE] Conditions OK, playing audio...');
        playRandomApneaAudio();
        console.log('✅ [ENFOQUE] Scheduling next audio...');
        startEnfoqueTimer(false);
      } else {
        console.log(`❌ [ENFOQUE] Conditions FAILED. State: ${state}, Expected: APNEA`);
        enfoqueTimerStarted.current = false; // Reset flag if stopped
      }
    }, randomDelay);
    
    const willFireAt = new Date(Date.now() + randomDelay).toLocaleTimeString();
    console.log(`✅ [ENFOQUE] Timer ID created: ${timers.current.enfoque}`);
    console.log(`✅ [ENFOQUE] Will fire at ${willFireAt} (in ${delaySeconds}s)`);
  };

  // Stop Enfoque timer
  const stopEnfoqueTimer = () => {
    console.log('🛑 [ENFOQUE] stopEnfoqueTimer called');
    if (timers.current.enfoque) {
      clearTimeout(timers.current.enfoque);
      timers.current.enfoque = null;
      console.log('🛑 [ENFOQUE] Timer cleared');
    }
    enfoqueTimerStarted.current = false; // Reset flag
    console.log('🔓 [ENFOQUE] Timer flag reset');
  };

  const saveVolumeConfig = () => {
    localStorage.setItem('breathAudioVolume', breathAudioVolume.toString());
    localStorage.setItem('bgAudioVolume', bgAudioVolume.toString());
    localStorage.setItem('customAudioVolume', customAudioVolume.toString());
    localStorage.setItem('customAudioEchoIntensity', customAudioEchoIntensity.toString());
    localStorage.setItem('apoyoEnabled', apoyoEnabled.toString());
    localStorage.setItem('enfoqueEnabled', enfoqueEnabled.toString());
    localStorage.setItem('apoyoVolume', apoyoVolume.toString());
    localStorage.setItem('enfoqueVolume', enfoqueVolume.toString());
    toast.success('Configuración guardada');
  };

  // Update impulse response based on echo intensity
  const updateEchoIntensity = (intensity) => {
    if (!audioContext.current || !convolver.current) return;
    
    try {
      const sampleRate = audioContext.current.sampleRate;
      const length = sampleRate * 2.5; // 2.5 seconds for smoother tail
      const impulse = audioContext.current.createBuffer(2, length, sampleRate);
      
      // Convert intensity (0-100) to multiplier (0-1)
      const multiplier = intensity / 100;
      
      // Create a softer, cleaner echo effect using discrete delays
      for (let channel = 0; channel < 2; channel++) {
        const channelData = impulse.getChannelData(channel);
        
        // Create multiple discrete echoes at specific intervals
        const echoDelays = [
          { time: 0.00, gain: 1.0 },    // Direct sound
          { time: 0.15, gain: 0.35 },   // First echo
          { time: 0.30, gain: 0.20 },   // Second echo
          { time: 0.50, gain: 0.12 },   // Third echo
          { time: 0.75, gain: 0.07 },   // Fourth echo
          { time: 1.10, gain: 0.04 },   // Fifth echo
          { time: 1.50, gain: 0.02 },   // Sixth echo
        ];
        
        echoDelays.forEach(echo => {
          const delayIndex = Math.floor(echo.time * sampleRate);
          if (delayIndex < length) {
            // Add a small amount of noise around each echo for natural sound
            const spread = Math.floor(sampleRate * 0.005); // 5ms spread
            for (let j = -spread; j <= spread; j++) {
              const index = delayIndex + j;
              if (index >= 0 && index < length) {
                const spreadFactor = Math.exp(-Math.abs(j) / spread * 2);
                channelData[index] += echo.gain * multiplier * spreadFactor * (Math.random() * 0.3 + 0.85);
              }
            }
          }
        });
        
        // Apply overall exponential decay for smoother tail
        for (let i = 0; i < length; i++) {
          const decayFactor = Math.exp(-i / (sampleRate * 0.8));
          channelData[i] *= decayFactor;
        }
      }
      
      convolver.current.buffer = impulse;
      console.log('✓ Echo intensity updated to:', intensity + '% (smooth echo)');
    } catch (error) {
      console.warn('Error updating echo intensity:', error);
    }
  };

  // Initialize Web Audio API for echo effect
  const initAudioContext = () => {
    if (!audioContext.current) {
      try {
        audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
        
        // Create gain node for volume control
        audioGainNode.current = audioContext.current.createGain();
        audioGainNode.current.gain.value = customAudioVolume / 100;
        
        // Create convolver for echo/reverb effect
        convolver.current = audioContext.current.createConvolver();
        
        // Initialize with current intensity
        updateEchoIntensity(customAudioEchoIntensity);
        
        console.log('✓ Web Audio API initialized for echo effect');
      } catch (error) {
        console.warn('Web Audio API not supported:', error);
      }
    }
  };

  // Unlock audio for mobile devices (autoplay policy) - SIMPLIFIED
  const unlockAudioForMobile = () => {
    console.log('🔓 [UNLOCK] Starting audio unlock for mobile...');
    
    try {
      // Only resume audio context if suspended
      if (audioContext.current && audioContext.current.state === 'suspended') {
        audioContext.current.resume().then(() => {
          console.log('✅ [UNLOCK] Audio context resumed');
        }).catch(err => {
          console.warn('[UNLOCK] Could not resume audio context:', err);
        });
      } else {
        console.log('ℹ️ [UNLOCK] Audio context already active or not available');
      }
      
      console.log('✅ [UNLOCK] Audio unlock complete (context only)');
      console.log('ℹ️ [UNLOCK] Individual audios will unlock on first play attempt');
    } catch (error) {
      console.error('[UNLOCK] Error in unlockAudioForMobile:', error);
    }
  };

  const startSession = () => {
    if (!mounted.current) return;
    
    // Unlock audio for mobile devices
    unlockAudioForMobile();
    
    // Clear ALL existing timers to prevent duplicates
    console.log('🧹 [SESSION] Clearing all timers before starting session');
    Object.keys(timers.current).forEach(key => {
      if (timers.current[key]) {
        clearTimeout(timers.current[key]);
        clearInterval(timers.current[key]);
      }
    });
    timers.current = {};
    
    // Reset timer flags
    apoyoTimerStarted.current = false;
    enfoqueTimerStarted.current = false;
    console.log('🔓 [SESSION] Timer flags reset');
    
    const isMeditation = pattern.name === "Meditación con audios SeptaSync";
    
    if (selectedBgAudio === 'none' && isMeditation) {
      toast.error('Selecciona un audio SeptaSync');
      return;
    }
    
    // Request wake lock to prevent screen from turning off
    requestWakeLock();
    
    setSessionStartTime(Date.now());
    playBackgroundAudio();
    
    // Pre-load ALL critical audios for mobile to unlock autoplay policy
    // Use sequential playback with slight delays to ensure all audios are unlocked
    const audiosToPreload = [
      { ref: finalizeAudioRef, name: 'finalizamos' },
      { ref: largamosun20AudioRef, name: 'largamosun20' },
      { ref: beginAudioRef, name: 'comenzamosdevueltaB' },
      { ref: largamostodoelireAudioRef, name: 'largamostodoelire' }
    ];
    
    // Add custom audio to preload list if exists
    if (user?.custom_audio_url && !isMeditation && customApneaAudioRef.current) {
      const proxyUrl = `${API}/audio/custom/${user.id}`;
      
      // Set the source if not already set
      if (!customApneaAudioRef.current.src || !customApneaAudioRef.current.src.includes(user.id)) {
        customApneaAudioRef.current.src = proxyUrl;
      }
      
      // Add to preload list
      audiosToPreload.push({ ref: customApneaAudioRef, name: 'custom_audio' });
      console.log('📱 [MOBILE FIX] Custom audio added to preload list');
    }
    
    const preloadAudiosSequentially = async () => {
      for (let i = 0; i < audiosToPreload.length; i++) {
        const { ref, name } = audiosToPreload[i];
        if (ref.current) {
          try {
            console.log(`📱 [MOBILE FIX] Pre-loading ${name}...`);
            
            // Load the audio first
            ref.current.load();
            
            // Play MUTED (completely silent)
            ref.current.muted = true;
            ref.current.currentTime = 0;
            
            await ref.current.play();
            console.log(`✅ [MOBILE FIX] ${name} playing (muted)`);
            
            // Let it play for only 20ms (very brief)
            await new Promise(resolve => setTimeout(resolve, 20));
            
            ref.current.pause();
            ref.current.currentTime = 0;
            ref.current.muted = false; // Unmute for actual playback
            console.log(`✅ [MOBILE FIX] ${name} unlocked`);
            
            // Small delay between audios
            await new Promise(resolve => setTimeout(resolve, 20));
          } catch (err) {
            console.log(`⚠️ [MOBILE FIX] ${name} failed:`, err.message);
          }
        }
      }
      console.log('✅ [MOBILE FIX] All audios ready');
    };
    
    // Start preloading and wait before starting session
    preloadAudiosSequentially().then(() => {
      // Give a very brief delay to ensure all audios are ready
      setTimeout(() => {
        if (!mounted.current) return;
        
        console.log('🎯 Starting session');
        
        if (isMeditation) {
          startMeditationSession();
        } else {
          startBreathingSession();
        }
      }, 100);
    });
  };
  
  const startMeditationSession = () => {
    if (!mounted.current) return;
    
    setState('MEDITATION');
    setMeditationTime(0);
    const target = meditationDuration * 60;
    
    timers.current.meditation = setInterval(() => {
      if (!mounted.current) return;
      setMeditationTime(prev => {
        if (prev + 1 >= target) {
          completeMeditation();
          return target;
        }
        return prev + 1;
      });
    }, 1000);
    
    // Session timer
    timers.current.session = setInterval(() => {
      if (mounted.current) setTotalTime(prev => prev + 1);
    }, 1000);
  };
  
  const startBreathingSession = () => {
    if (!mounted.current) return;
    
    // Inicializar listas de audios reproducidos
    playedEnergiaAudios.current = [];
    playedApneaAudios.current = [];
    
    setCurrentBreath(1);
    setState('INHALE');
    startBreathCycle(1);
    
    // Start Apoyo timer if enabled (first audio at 10s)
    console.log('🎬 Checking if Apoyo is enabled... apoyoEnabled:', apoyoEnabled, 'apoyoEnabledRef:', apoyoEnabledRef.current);
    if (apoyoEnabledRef.current) {
      console.log('✅ Starting Apoyo timer from startSession');
      startApoyoTimer(true);
    } else {
      console.log('❌ Apoyo not enabled, skipping timer');
    }
    
    // Session timer
    timers.current.session = setInterval(() => {
      if (mounted.current) setTotalTime(prev => prev + 1);
    }, 1000);
  };

  const startBreathCycle = (breathNumber) => {
    if (!mounted.current) return;
    
    // Clear any existing breath timer to prevent overlapping
    if (timers.current.breath) clearTimeout(timers.current.breath);
    
    const isLast = breathNumber === pattern.breaths_count;
    const isSecondToLast = breathNumber === pattern.breaths_count - 1;
    const isLastTwo = breathNumber >= pattern.breaths_count - 1; // Last 2 breaths
    const isLastThree = breathNumber >= pattern.breaths_count - 2; // Last 3 breaths
    const patternName = pattern.name;
    
    // Detener Apoyo timer cuando quedan 3 respiraciones (para que no suene cerca de la apnea)
    if (isLastThree && apoyoEnabledRef.current) {
      console.log('🛑 Deteniendo Apoyo timer (quedan 3 respiraciones antes de apnea)');
      stopApoyoTimer();
    }
    
    // Audio logic based on pattern and breath position
    if (patternName === "Pre-Entrenamiento") {
      // Pre-Entrenamiento: 63 respiraciones, 1 ronda
      if (isLast) {
        // Respiración 63 (última): largamosun20.mp3
        playAudio(largamosun20AudioRef);
      } else {
        // Respiraciones 1-62: audio normal de respiración
        playAudio(breathFire1and2AudioRef);
      }
    } else if (patternName === "BreathFire 3") {
      // BreathFire 3: 20 respiraciones
      if (isLast) {
        // Respiración 20 (última): largamosun20.mp3
        playAudio(largamosun20AudioRef);
      } else {
        // Respiraciones 1-19: audio normal
        playAudio(breathFire3AudioRef);
      }
    } else if (patternName === "BreathFire 4") {
      if (isLast) {
        // Última respiración: largamosun20.mp3
        playAudio(largamosun20AudioRef);
      } else {
        playAudio(breathFire4AudioRef);
      }
    } else {
      // BreathFire 1, 2 and other patterns
      if (isLast) {
        // Última respiración: largamosun20.mp3
        playAudio(largamosun20AudioRef);
      } else {
        // Respiraciones normales
        playAudio(breathFire1and2AudioRef);
      }
    }
    
    setState('INHALE');
    
    // Since audio now contains both inhale and exhale, calculate total breath duration
    const totalBreathDuration = (pattern.inhale_duration + pattern.exhale_duration) * 1000;
    
    // Transition to EXHALE state at inhale_duration
    timers.current.inhaleState = setTimeout(() => {
      if (mounted.current) setState('EXHALE');
    }, pattern.inhale_duration * 1000);
    
    // Move to next breath after full cycle
    timers.current.breath = setTimeout(() => {
      if (!mounted.current) return;
      
      if (breathNumber < pattern.breaths_count) {
        setCurrentBreath(breathNumber + 1);
        startBreathCycle(breathNumber + 1);
      } else {
        enterApnea();
      }
    }, totalBreathDuration);
  };

  const enterApnea = () => {
    if (!mounted.current) return;
    
    // Clear all breath-related timers to prevent overlapping
    if (timers.current.breath) clearTimeout(timers.current.breath);
    if (timers.current.inhaleState) clearTimeout(timers.current.inhaleState);
    if (timers.current.apnea) clearInterval(timers.current.apnea);
    if (timers.current.block) clearTimeout(timers.current.block);
    if (timers.current.customAudioDelay) clearTimeout(timers.current.customAudioDelay);
    if (timers.current.fadeIn) clearInterval(timers.current.fadeIn);
    
    // Stop Apoyo timer (breathing audios)
    stopApoyoTimer();
    
    setState('APNEA');
    setApneaTime(0);
    setIsBlocked(true);
    
    // DO NOT play Largamosun20.mp3 here - it plays in penultimate breath
    
    timers.current.apnea = setInterval(() => {
      if (mounted.current) setApneaTime(prev => prev + 1);
    }, 1000);
    
    // Play custom apnea audio with fade-in from 0 to full volume over 2 seconds
    // (EXCEPT for meditation pattern)
    const isMeditationPattern = pattern.name === "Meditación con audios SeptaSync";
    console.log('Entering apnea, user:', user);
    console.log('Pattern:', pattern.name, 'isMeditation:', isMeditationPattern);
    
    if (user?.custom_audio_url && !isMeditationPattern) {
      console.log('Custom audio URL found:', user.custom_audio_url);
      console.log('🎵 Playing custom audio immediately with fade-in...');
      
      // Play immediately to respect mobile autoplay policy
      playCustomApneaAudio(true); // Pass true to indicate fade-in
    } else {
      if (isMeditationPattern) {
        console.log('❌ Skipping custom audio (meditation pattern)');
      } else {
        console.log('No custom audio URL for this user');
      }
    }
    
    // Resetear lista de audios de enfoque para nueva apnea
    playedApneaAudios.current = [];
    console.log('🔄 Nueva apnea: reseteando audios de enfoque reproducidos');
    
    // Start Enfoque timer (apnea audios) if enabled (first audio at 10s)
    console.log('🎬 Checking if Enfoque is enabled... enfoqueEnabled:', enfoqueEnabled, 'enfoqueEnabledRef:', enfoqueEnabledRef.current);
    if (enfoqueEnabledRef.current) {
      console.log('✅ Starting Enfoque timer from enterApnea');
      startEnfoqueTimer(true);
    } else {
      console.log('❌ Enfoque not enabled, skipping timer');
    }
    
    timers.current.block = setTimeout(() => {
      if (mounted.current) setIsBlocked(false);
    }, 2000);
  };

  const handleScreenTap = () => {
    if (isBlocked || state === 'MEDITATION' || !mounted.current) return;

    if (state === 'INHALE' || state === 'EXHALE') {
      // Double tap to skip to apnea early
      setClickCount(prev => prev + 1);
      
      if (timers.current.click) clearTimeout(timers.current.click);
      
      if (clickCount + 1 >= 2) {
        // Clear all breath timers before entering apnea
        if (timers.current.breath) clearTimeout(timers.current.breath);
        if (timers.current.inhaleState) clearTimeout(timers.current.inhaleState);
        setClickCount(0);
        enterApnea();
      } else {
        timers.current.click = setTimeout(() => {
          if (mounted.current) setClickCount(0);
        }, 300);
      }
    } else if (state === 'APNEA') {
      // Double tap to exit apnea and start countdown
      setClickCount(prev => prev + 1);
      
      if (timers.current.click) clearTimeout(timers.current.click);
      
      if (clickCount + 1 >= 2) {
        setIsBlocked(true);
        timers.current.block = setTimeout(() => {
          if (mounted.current) setIsBlocked(false);
        }, 2000);

        setApneaRecords(prev => [...prev, {
          round_number: currentRound,
          duration: apneaTime
        }]);
        
        if (timers.current.apnea) clearInterval(timers.current.apnea);
        if (timers.current.customAudioDelay) clearTimeout(timers.current.customAudioDelay);
        if (timers.current.fadeIn) clearInterval(timers.current.fadeIn);
        stopCustomApneaAudio();
        startCountdown();
        setClickCount(0);
      } else {
        timers.current.click = setTimeout(() => {
          if (mounted.current) setClickCount(0);
        }, 300);
      }
    }
  };

  const handleStopClick = () => {
    if (!mounted.current) return;
    
    setStopClickCount(prev => prev + 1);

    if (timers.current.stopClick) clearTimeout(timers.current.stopClick);

    if (stopClickCount + 1 >= 2) {
      Object.values(timers.current).forEach(t => {
        if (t) {clearTimeout(t); clearInterval(t);}
      });
      stopAllAudios();
      releaseWakeLock();
      toast.info('Sesión detenida');
      navigate('/');
    } else {
      timers.current.stopClick = setTimeout(() => {
        if (mounted.current) setStopClickCount(0);
      }, 500);
    }
  };

  const startCountdown = () => {
    if (!mounted.current) return;
    
    // Clear any existing countdown timers
    if (timers.current.countdown) clearInterval(timers.current.countdown);
    if (timers.current.beginAudio) clearTimeout(timers.current.beginAudio);
    
    // Stop Enfoque timer (apnea audios)
    stopEnfoqueTimer();
    
    setState('COUNTDOWN');
    setCountdown(15);
    setIsBlocked(true);
    
    // Check if this is the last round
    const isLastRound = currentRound >= pattern.rounds;
    const isBreathFire4 = pattern.name === "BreathFire 4";
    
    if (isLastRound) {
      // Last round: NO comenzamosdevueltaB.mp3, only finalizamos.mp3 at the end
      // Silent countdown
      console.log('Last round: Silent countdown');
    } else if (isBreathFire4) {
      // BreathFire 4 (not last round): Only play comenzamosdevueltaB.mp3 when 3 seconds remain (second 12)
      // No audio at the beginning of countdown
      timers.current.beginAudio = setTimeout(() => {
        if (mounted.current) {
          console.log('Playing comenzamosdevueltaB.mp3 for BreathFire 4 (3 seconds remaining)');
          playAudio(beginAudioRef);
        }
      }, 12000); // At second 12 (3 seconds remaining)
    } else {
      // Other patterns (not last round): Play Largamostodoelire.mp3 at beginning
      console.log('Playing Largamostodoelire.mp3');
      playAudio(largamostodoelireAudioRef);
      
      // And play comenzamosdevueltaB.mp3 when 3 seconds remain (second 12)
      timers.current.beginAudio = setTimeout(() => {
        if (mounted.current) {
          console.log('Playing comenzamosdevueltaB.mp3 (3 seconds remaining)');
          playAudio(beginAudioRef);
        }
      }, 12000); // At second 12 (3 seconds remaining)
    }
    
    timers.current.countdown = setInterval(() => {
      if (!mounted.current) return;
      
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timers.current.countdown);
          if (timers.current.beginAudio) clearTimeout(timers.current.beginAudio);
          setTimeout(() => {
            if (mounted.current) setIsBlocked(false);
          }, 500);
          nextRound();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const nextRound = () => {
    if (!mounted.current) return;
    
    const nextRoundNumber = currentRound + 1;
    
    if (nextRoundNumber <= pattern.rounds) {
      setCurrentRound(nextRoundNumber);
      setCurrentBreath(1);
      setState('INHALE');
      setIsBlocked(true);
      
      // Add 1 second pause before starting new round to prevent audio cutoff
      timers.current.pauseBeforeRound = setTimeout(() => {
        if (mounted.current) {
          // Resetear listas de audios reproducidos para nueva ronda
          console.log('🔄 Nueva ronda: reseteando audios reproducidos');
          playedEnergiaAudios.current = [];
          playedApneaAudios.current = [];
          
          startBreathCycle(1);
          
          // Restart Apoyo timer if enabled (first audio at 10s for new round)
          console.log('🎬 Checking Apoyo for new round... apoyoEnabledRef:', apoyoEnabledRef.current);
          if (apoyoEnabledRef.current) {
            console.log('✅ Restarting Apoyo timer for new round');
            startApoyoTimer(true);
          }
          
          timers.current.block = setTimeout(() => {
            if (mounted.current) setIsBlocked(false);
          }, 1000);
        }
      }, 1000);
    } else {
      completeSession();
    }
  };

  const completeMeditation = async () => {
    if (!mounted.current) return;
    
    setState('COMPLETE');
    Object.values(timers.current).forEach(t => {
      if (t) {clearTimeout(t); clearInterval(t);}
    });
    stopAllAudios();
    releaseWakeLock();
    playAudio(finalizeAudioRef);
    
    try {
      const headers = {};
      const authHeaders = getAuthHeaders();
      if (authHeaders.Authorization) {
        headers.Authorization = authHeaders.Authorization;
      }
      
      await axios.post(`${API}/sessions`, {
        pattern_id: pattern.id,
        pattern_name: pattern.name,
        completed_rounds: 1,
        total_time: Math.floor((Date.now() - sessionStartTime) / 1000),
        apnea_records: [],
        background_audio: selectedBgAudio
      }, { headers });
      
      if (mounted.current) toast.success('¡Completado!');
    } catch (error) {}
  };

  const completeSession = async () => {
    if (!mounted.current) return;
    
    setState('COMPLETE');
    Object.values(timers.current).forEach(t => {
      if (t) {clearTimeout(t); clearInterval(t);}
    });
    stopAllAudios();
    releaseWakeLock();
    playAudio(finalizeAudioRef);
    
    try {
      const headers = {};
      const authHeaders = getAuthHeaders();
      if (authHeaders.Authorization) {
        headers.Authorization = authHeaders.Authorization;
      }
      
      await axios.post(`${API}/sessions`, {
        pattern_id: pattern.id,
        pattern_name: pattern.name,
        completed_rounds: currentRound,
        total_time: Math.floor((Date.now() - sessionStartTime) / 1000),
        apnea_records: apneaRecords,
        background_audio: selectedBgAudio
      }, { headers });
      
      if (mounted.current) toast.success('¡Completado!');
    } catch (error) {}
  };

  const resetSession = () => {
    Object.values(timers.current).forEach(t => {
      if (t) {clearTimeout(t); clearInterval(t);}
    });
    stopAllAudios();
    releaseWakeLock();
    setState('READY');
    setCurrentBreath(0);
    setCurrentRound(1);
    setCountdown(15);
    setApneaTime(0);
    setMeditationTime(0);
    setApneaRecords([]);
    setTotalTime(0);
    setClickCount(0);
    setStopClickCount(0);
    setIsBlocked(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getBgAudioSrc = () => {
    const map = {
      'balance': '/audio/balance.mp3',
      'delta': '/audio/delta.mp3',
      'focus': '/audio/focus.mp3',
      'gamma': '/audio/gamma.mp3',
      'study': '/audio/study.mp3'
    };
    return map[selectedBgAudio] || null;
  };

  if (!pattern) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const isMeditationMode = pattern.name === "Meditación con audios SeptaSync";
  const getStateText = () => {
    if (state === 'READY') return 'Listo para comenzar';
    if (state === 'INHALE') return 'INHALA';
    if (state === 'EXHALE') return 'EXHALA';
    if (state === 'APNEA') return 'APNEA - Mantén';
    if (state === 'COUNTDOWN') return 'Preparando siguiente ronda';
    if (state === 'MEDITATION') return 'Meditando...';
    if (state === 'COMPLETE') return isMeditationMode ? '¡Meditación Completada!' : '¡Sesión Completada!';
    return '';
  };

  const getSphereScale = () => {
    if (state === 'INHALE') return 'scale-150';
    if (state === 'EXHALE') return 'scale-75';
    return 'scale-100';
  };

  const getRemainingMeditationTime = () => {
    const remaining = (meditationDuration * 60) - meditationTime;
    return formatTime(Math.max(0, remaining));
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <Button onClick={() => navigate('/')} variant="outline" className="rounded-full text-sm">
            <ArrowLeft size={18} className="mr-2" />Volver
          </Button>

          {state !== 'READY' && state !== 'COMPLETE' && (
            <Button
              onClick={handleStopClick}
              variant={stopClickCount >= 1 ? "destructive" : "outline"}
              className="rounded-full text-sm"
            >
              <StopCircle size={18} className="mr-2" />
              {stopClickCount >= 1 ? 'Confirmar' : 'Detener'}
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-4" onClick={handleScreenTap}>
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold mb-2">{pattern.name}</h2>
          {!isMeditationMode && <p className="text-sm text-gray-600">Ronda {currentRound} de {pattern.rounds}</p>}
        </div>

        {state === 'READY' && (
          <div className="mb-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="glass rounded-xl p-4">
              <label className="flex items-center text-sm font-medium mb-2">
                <Music size={16} className="mr-2" />
                Audios SeptaSync {isMeditationMode && <span className="text-red-500 ml-1">*</span>}
              </label>
              <Select value={selectedBgAudio} onValueChange={setSelectedBgAudio}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BACKGROUND_AUDIOS.map(audio => (
                    <SelectItem key={audio.value} value={audio.value}>{audio.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isMeditationMode && (
              <div className="glass rounded-xl p-4">
                <Label>Duración (minutos)</Label>
                <Input
                  type="number"
                  min="1"
                  max="120"
                  value={meditationDuration}
                  onChange={(e) => setMeditationDuration(parseInt(e.target.value) || 10)}
                  className="mt-2"
                />
              </div>
            )}

            {!isMeditationMode && (
              <div className="glass rounded-xl p-4 space-y-4">
                <div>
                  <label className="flex items-center justify-between text-sm font-medium mb-2">
                    <span>Vol. Respiración</span>
                    <span className="text-[#77C9D4]">{breathAudioVolume}%</span>
                  </label>
                  <Slider value={[breathAudioVolume]} onValueChange={(v) => setBreathAudioVolume(v[0])} max={100} step={5} />
                </div>
                <div>
                  <label className="flex items-center justify-between text-sm font-medium mb-2">
                    <span>Vol. Audios SeptaSync</span>
                    <span className="text-[#77C9D4]">{bgAudioVolume}%</span>
                  </label>
                  <Slider value={[bgAudioVolume]} onValueChange={(v) => setBgAudioVolume(v[0])} max={100} step={5} />
                </div>
                {user?.custom_audio_url && (
                  <>
                    <div>
                      <label className="flex items-center justify-between text-sm font-medium mb-2">
                        <span>Vol. Frases de Afirmación</span>
                        <span className="text-[#77C9D4]">{customAudioVolume}%</span>
                      </label>
                      <Slider value={[customAudioVolume]} onValueChange={(v) => setCustomAudioVolume(v[0])} max={100} step={5} />
                    </div>
                    <div>
                      <label className="flex items-center justify-between text-sm font-medium mb-2">
                        <span>Intensidad de Eco</span>
                        <span className="text-[#77C9D4]">{customAudioEchoIntensity}%</span>
                      </label>
                      <Slider 
                        value={[customAudioEchoIntensity]} 
                        onValueChange={(v) => setCustomAudioEchoIntensity(v[0])} 
                        max={100} 
                        step={5}
                        className="mb-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">0% = sin eco, 100% = eco máximo</p>
                    </div>
                  </>
                )}
                
                {/* Apoyo Toggle and Volume - TEMPORALMENTE DESHABILITADO */}
                {false && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium">Apoyo (Respiraciones)</label>
                    <Switch 
                      checked={apoyoEnabled} 
                      onCheckedChange={setApoyoEnabled}
                    />
                  </div>
                  {apoyoEnabled && (
                    <div>
                      <label className="flex items-center justify-between text-sm font-medium mb-2">
                        <span>Vol. Apoyo</span>
                        <span className="text-[#77C9D4]">{apoyoVolume}%</span>
                      </label>
                      <Slider value={[apoyoVolume]} onValueChange={(v) => setApoyoVolume(v[0])} max={100} step={5} />
                      <p className="text-xs text-gray-500 mt-1">Audios aleatorios cada 20-54s</p>
                    </div>
                  )}
                </div>
                )}
                
                {/* Enfoque Toggle and Volume - TEMPORALMENTE DESHABILITADO */}
                {false && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium">Enfoque (Apneas)</label>
                    <Switch 
                      checked={enfoqueEnabled} 
                      onCheckedChange={setEnfoqueEnabled}
                    />
                  </div>
                  {enfoqueEnabled && (
                    <div>
                      <label className="flex items-center justify-between text-sm font-medium mb-2">
                        <span>Vol. Enfoque</span>
                        <span className="text-[#77C9D4]">{enfoqueVolume}%</span>
                      </label>
                      <Slider value={[enfoqueVolume]} onValueChange={(v) => setEnfoqueVolume(v[0])} max={100} step={5} />
                      <p className="text-xs text-gray-500 mt-1">Audios aleatorios durante apnea</p>
                    </div>
                  )}
                </div>
                )}
                
                <Button 
                  onClick={saveVolumeConfig} 
                  className="w-full bg-[#77C9D4] hover:bg-[#5AB4C1] !text-white dark:!text-black rounded-full"
                >
                  Guardar Configuración
                </Button>
              </div>
            )}

            {isMeditationMode && selectedBgAudio !== 'none' && (
              <div className="glass rounded-xl p-4 space-y-4">
                <div>
                  <label className="flex items-center justify-between text-sm font-medium mb-2">
                    <span>Vol. Audios SeptaSync</span>
                    <span className="text-[#77C9D4]">{bgAudioVolume}%</span>
                  </label>
                  <Slider value={[bgAudioVolume]} onValueChange={(v) => setBgAudioVolume(v[0])} max={100} step={5} />
                </div>
                <Button 
                  onClick={saveVolumeConfig} 
                  className="w-full bg-[#77C9D4] hover:bg-[#5AB4C1] !text-white dark:!text-black rounded-full"
                >
                  Guardar Configuración
                </Button>
              </div>
            )}
          </div>
        )}

        {!isMeditationMode && state !== 'READY' && state !== 'COMPLETE' && (
          <div className="relative mb-6">
            <div className="sphere-container">
              <div 
                className={`sphere w-48 h-48 transition-transform ease-in-out ${getSphereScale()}`}
                style={{
                  transitionDuration: state === 'INHALE' ? `${pattern.inhale_duration}s` : 
                                      state === 'EXHALE' ? `${pattern.exhale_duration}s` : '0.5s'
                }}
              ></div>
              <div className="counter-text text-5xl">
                {state === 'COUNTDOWN' ? countdown : state === 'APNEA' ? formatTime(apneaTime) : currentBreath}
              </div>
            </div>
          </div>
        )}

        {isMeditationMode && state === 'MEDITATION' && (
          <div className="mb-6 text-center">
            <div className="glass rounded-3xl p-8">
              <div className="text-6xl font-bold text-[#77C9D4] mb-4">{getRemainingMeditationTime()}</div>
              <p className="text-sm text-gray-600">Tiempo restante</p>
            </div>
          </div>
        )}

        <div className="text-center mb-6">
          <p className="text-2xl font-bold text-[#77C9D4]">{getStateText()}</p>
          {(state === 'INHALE' || state === 'EXHALE') && (
            <p className="text-xs text-gray-600 mt-2">Doble toque para saltar a apnea</p>
          )}
          {state === 'APNEA' && (
            <p className="text-xs text-gray-600 mt-2">{isBlocked ? 'Espera...' : 'Doble toque para continuar'}</p>
          )}
        </div>

        {!isMeditationMode && state !== 'READY' && state !== 'COMPLETE' && (
          <div className="text-center mb-4">
            <p className="text-base text-gray-600">Respiración {currentBreath} de {pattern.breaths_count}</p>
          </div>
        )}

        <div className="flex flex-col gap-3 w-full max-w-md px-4">
          {state === 'READY' && (
            <Button
              onClick={(e) => {e.stopPropagation(); startSession();}}
              className="bg-[#77C9D4] hover:bg-[#5AB4C1] !text-white dark:!text-black rounded-full px-8 py-6"
            >
              <Play size={24} className="mr-2" />Comenzar
            </Button>
          )}
          
          {state !== 'READY' && state !== 'COMPLETE' && (
            <Button
              onClick={(e) => {e.stopPropagation(); resetSession();}}
              variant="outline"
              className="rounded-full px-6 py-6"
            >
              <RotateCcw size={20} className="mr-2" />Reiniciar
            </Button>
          )}

          {state === 'COMPLETE' && (
            <>
              <Button onClick={(e) => {e.stopPropagation(); navigate('/');}} className="bg-[#77C9D4] hover:bg-[#5AB4C1] !text-white dark:!text-black rounded-full px-6 py-6">
                Finalizar
              </Button>
              <Button onClick={(e) => {e.stopPropagation(); resetSession();}} variant="outline" className="rounded-full px-6 py-6">
                <RotateCcw size={20} className="mr-2" />{isMeditationMode ? 'Nueva Meditación' : 'Nueva Sesión'}
              </Button>
            </>
          )}
        </div>

        {state === 'COMPLETE' && (
          <div className="mt-6 glass rounded-2xl p-4 text-center w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">¡Excelente trabajo!</h3>
            <div className="space-y-2 text-sm">
              {!isMeditationMode && <p className="text-gray-600">Rondas: <span className="font-bold">{currentRound}</span></p>}
              <p className="text-gray-600">Tiempo: <span className="font-bold">{formatTime(totalTime)}</span></p>
              {apneaRecords.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="font-semibold mb-2">Apneas:</p>
                  {apneaRecords.map((r, i) => (
                    <p key={i} className="text-sm text-gray-600">
                      Ronda {r.round_number}: <span className="font-bold text-[#77C9D4]">{formatTime(Math.floor(r.duration))}</span>
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <audio ref={breathFire1and2AudioRef} src="/audio/breathfire1y2B.mp3" preload="auto" />
      <audio ref={breathFire3AudioRef} src="/audio/inalaexala8.mp3" preload="auto" />
      <audio ref={breathFire4AudioRef} src="/audio/breathfire4.mp3" preload="auto" />
      <audio ref={largamosun20AudioRef} src="/audio/Largamosun20.mp3" preload="auto" />
      <audio ref={largamostodoelireAudioRef} src="/audio/Largamostodoelire.mp3" preload="auto" />
      <audio ref={beginAudioRef} src="/audio/comenzamosdevueltaB.mp3" preload="auto" />
      <audio ref={finalizeAudioRef} src="/audio/finalizamos.mp3" preload="auto" />
      {getBgAudioSrc() && <audio ref={backgroundAudioRef} src={getBgAudioSrc()} preload="none" />}
      {user?.custom_audio_url && (
        <audio 
          ref={customApneaAudioRef} 
          crossOrigin="anonymous"
          preload="metadata"
          onError={(e) => console.error('Error loading custom audio:', e)}
          onLoadedData={() => console.log('Custom audio loaded successfully')}
        />
      )}
      
      {/* Apoyo audios (energia 1-8) */}
      {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
        <audio 
          key={`energia-${i}`}
          ref={el => energiaAudioRefs.current[i - 1] = el}
          src={`/audio/energia${i}.mp3`}
          preload="auto"
        />
      ))}
      
      {/* Enfoque audios (apnea 1-12) */}
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(i => (
        <audio 
          key={`apnea-${i}`}
          ref={el => apneaAudioRefs.current[i - 1] = el}
          src={`/audio/apnea${i}.mp3`}
          preload="auto"
        />
      ))}
    </div>
  );
};

export default BreathingSession;
